import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal, notification, Select } from 'antd';
import { FileSpreadsheet, Plus, Upload as UploadIcon } from 'lucide-react';
import {
  createEquipmentProfile,
  downloadBlob,
  exportEquipmentProfilesXlsx,
  fetchEquipmentProfiles,
  importEquipmentProfilesXlsx,
  retireEquipmentProfile,
  updateEquipmentProfile,
} from '../../services/arcWelding';
import { useArcWeldingFormOptions } from '../../hooks/useArcWeldingFormOptions';
import {
  fileToBase64,
  AW_FILTER_LABELS,
  normalizePagedResponse,
} from '../../utils/arcWeldingVerificationUi';
import ArcWeldingEquipmentProfileForm from './ArcWeldingEquipmentProfileForm';
import ArcWeldingListShell, {
  AwFilterGroup,
  AwFilterSearch,
  AwFilterSelect,
  buildActionsColumn,
  buildServerPagination,
  buildTextColumn,
  useDebouncedSearch,
} from './ArcWeldingListShell';
import './ArcWelding.css';

const { Option } = Select;

const ArcWeldingEquipmentProfilesPage = () => {
  const { options } = useArcWeldingFormOptions();
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [searchInput, debouncedSearch, setSearchInput] = useDebouncedSearch();
  const [equipmentType, setEquipmentType] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState({});
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const importInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await fetchEquipmentProfiles({
        search: debouncedSearch || undefined,
        equipmentType: equipmentType || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        pageNumber,
        pageSize,
      });
      const paged = normalizePagedResponse(payload);
      setItems(paged.items);
      setTotal(paged.total);
    } catch (err) {
      notification.error({
        message: 'Failed to load equipment profiles',
        description: err?.message || err?.error || String(err),
      });
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, equipmentType, isActiveFilter, pageNumber, pageSize]);

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch, equipmentType, isActiveFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleExport = async () => {
    try {
      const blob = await exportEquipmentProfilesXlsx();
      downloadBlob(blob, 'arc-welding-equipment-profiles.xlsx');
    } catch (err) {
      notification.error({ message: 'Export failed', description: String(err?.message || err) });
    }
  };

  const handleImport = async (file) => {
    setImporting(true);
    try {
      const fileBase64 = await fileToBase64(file);
      const result = await importEquipmentProfilesXlsx(fileBase64);
      notification.success({
        message: 'Import complete',
        description: `Imported: ${result?.imported ?? 0} profile(s)`,
      });
      load();
    } catch (err) {
      notification.error({ message: 'Import failed', description: String(err?.message || err) });
    } finally {
      setImporting(false);
    }
    return false;
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await updateEquipmentProfile(editing.id, values);
        notification.success({ message: 'Profile updated' });
      } else {
        await createEquipmentProfile(values);
        notification.success({ message: 'Profile created' });
      }
      setFormOpen(false);
      setEditing(null);
      load();
    } catch (err) {
      notification.error({ message: 'Save failed', description: String(err?.message || err) });
    } finally {
      setSaving(false);
    }
  };

  const handleRetire = useCallback((record) => {
    Modal.confirm({
      className: 'heatprofile-modal',
      title: 'Retire Equipment Profile?',
      content: `"${record.profileName}" will be marked inactive.`,
      centered: true,
      onOk: async () => {
        try {
          await retireEquipmentProfile(record.id);
          notification.success({ message: 'Profile retired' });
          load();
        } catch (err) {
          notification.error({ message: 'Retire failed', description: String(err?.message || err) });
        }
      },
    });
  }, [load]);

  const filteredItems = useMemo(() => {
    const entries = Object.entries(columnFilters).filter(([, v]) => v);
    if (!entries.length) return items;
    return items.filter((row) =>
      entries.every(([key, val]) =>
        String(row[key] ?? '').toLowerCase().includes(String(val).toLowerCase())
      )
    );
  }, [items, columnFilters]);

  const columns = useMemo(
    () => [
      buildTextColumn('profileName', 'Profile Name', {
        filterValue: columnFilters.profileName,
        onFilterChange: (v) => setColumnFilters((p) => ({ ...p, profileName: v })),
      }),
      buildTextColumn('equipmentType', 'Type', {
        filterValue: columnFilters.equipmentType,
        onFilterChange: (v) => setColumnFilters((p) => ({ ...p, equipmentType: v })),
      }),
      buildTextColumn('manufacturer', 'Manufacturer', {
        filterValue: columnFilters.manufacturer,
        onFilterChange: (v) => setColumnFilters((p) => ({ ...p, manufacturer: v })),
      }),
      buildTextColumn('model', 'Model', {
        filterValue: columnFilters.model,
        onFilterChange: (v) => setColumnFilters((p) => ({ ...p, model: v })),
      }),
      {
        title: 'Current (A)',
        key: 'current',
        align: 'left',
        width: 140,
        render: (_, r) =>
          r.currentMinA != null || r.currentMaxA != null
            ? `${r.currentMinA ?? '—'} – ${r.currentMaxA ?? '—'}`
            : '—',
      },
      buildTextColumn('isActive', 'Active', {
        render: (v) => (v === false ? 'No' : 'Yes'),
        width: 90,
      }),
      buildActionsColumn({
        onEdit: (record) => {
          setEditing(record);
          setFormOpen(true);
        },
        onDelete: handleRetire,
        canDelete: (record) => record.isActive !== false,
      }),
    ],
    [columnFilters, handleRetire]
  );

  const filterBar = (
    <>
      <AwFilterGroup label={AW_FILTER_LABELS.search} className="aw-filter-group--search">
        <AwFilterSearch
          placeholder="Name, Manufacturer, Model"
          value={searchInput}
          onChange={setSearchInput}
        />
      </AwFilterGroup>
      <AwFilterGroup label={AW_FILTER_LABELS.equipmentType} className="aw-filter-group--select">
        <AwFilterSelect
          placeholder="All Types"
          value={equipmentType}
          onChange={(v) => setEquipmentType(v)}
        >
          {options.equipmentType?.map((t) => (
            <Option key={t} value={t}>{t}</Option>
          ))}
        </AwFilterSelect>
      </AwFilterGroup>
      <AwFilterGroup label={AW_FILTER_LABELS.status} className="aw-filter-group--select-sm">
        <AwFilterSelect
          placeholder="All Statuses"
          value={isActiveFilter}
          onChange={(v) => setIsActiveFilter(v)}
        >
          <Option value="true">Active</Option>
          <Option value="false">Inactive</Option>
        </AwFilterSelect>
      </AwFilterGroup>
    </>
  );

  return (
    <>
      <ArcWeldingListShell
        title="Equipment Profiles"
        description="Data Rev 1 register — arc welding power sources and wire feeders."
        count={total}
        headerActions={[
          {
            key: 'export',
            label: 'Download As Excel',
            variant: 'tertiary',
            icon: FileSpreadsheet,
            onClick: handleExport,
          },
          {
            key: 'import',
            label: importing ? 'Importing...' : 'Import Excel',
            variant: 'tertiary',
            icon: UploadIcon,
            onClick: () => importInputRef.current?.click(),
            disabled: importing,
          },
          {
            key: 'add',
            label: 'Add New',
            variant: 'primary',
            icon: Plus,
            onClick: () => {
              setEditing(null);
              setFormOpen(true);
            },
          },
        ]}
        filterBar={filterBar}
        columns={columns}
        dataSource={filteredItems}
        loading={loading}
        scroll={{ x: 1400 }}
        pagination={buildServerPagination({
          total,
          pageNumber,
          pageSize,
          onChange: (page, size) => {
            setPageNumber(page);
            setPageSize(size);
          },
        })}
      />

      <input
        ref={importInputRef}
        type="file"
        accept=".xlsx,.xls"
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleImport(file);
          e.target.value = '';
        }}
      />

      <ArcWeldingEquipmentProfileForm
        open={formOpen}
        initialValues={editing}
        onCancel={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={handleSave}
        loading={saving}
      />
    </>
  );
};

export default ArcWeldingEquipmentProfilesPage;
